import React, { useRef, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SparkLine } from "./chart-components/SparkLine.jsx";

import { formatValue, formatPercentage } from "./Utils";

export const KPI = (props) => {
	const { title, currentValue, previousValue, format, showChange, trendData, className, changeTitle, showForecast } = props;

	// Handle null/undefined previousValue - treat as 0 for calculation
	const safePreviousValue = previousValue ?? 0;
	const change = currentValue - safePreviousValue;
	// If previousValue is 0, null, or undefined, show 100% (or 0% if currentValue is also 0)
	const changePercentage = (safePreviousValue !== 0 ? (change / safePreviousValue) * 100 : (currentValue > 0 ? 100 : 0));
	const isPositiveChange = change >= 0;
	const width = "w-full";

	const containerRef = useRef(null);
	const [sparklineWidth, setSparklineWidth] = useState(100);

	useEffect(() => {
		const updateWidth = () => {
			if (containerRef.current) {
				// offsetWidth includes border, subtract 2px (1px border on each side)
				// to fit sparkline exactly within the border
				const containerWidth = containerRef.current.offsetWidth - 2;
				setSparklineWidth(containerWidth);
			}
		};

		updateWidth();
		
		// Use ResizeObserver for more accurate width tracking
		const resizeObserver = new ResizeObserver(() => {
			updateWidth();
		});

		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		window.addEventListener('resize', updateWidth);
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updateWidth);
		};
	}, []);

	return (
		<div ref={containerRef} data-kpi-component="true" className={`${className || width} h-full rounded-card min-w-[250px]`}>
			<div className={`bg-card border border-card-bd h-full px-4 pt-6 ${trendData ? 'pb-0' : 'pb-4'} rounded-card cursor-default flex flex-col justify-center overflow-hidden`}>
				<div className="flex flex-col flex-1">
					<div className="text-text-2 text-xs ml-2">
						<span className="">{title}</span>
					</div>
					<div className={`flex flex-row items-end ${!trendData ? 'mb-[24px]' : ''}`}>
						<div
							className="text-text text-2xl font-semibold ml-2"
							style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
						>
							{format === "percentage" ? formatPercentage(currentValue, false) : formatValue(currentValue, format, false)}
						</div>
						{showChange && (
							<div className={`text-sm whitespace-nowrap text-right ml-auto flex flex-col pb-1 pl-4`}>
								<div className="flex flex-row items-center justify-end gap-1.5">
									{isPositiveChange ? (
										<FontAwesomeIcon
											icon={icon({
												name: "arrow-trend-up",
												family: "sharp",
												style: "solid",
											})}
											className="text-success"
										/>
									) : (
										<FontAwesomeIcon
											icon={icon({
												name: "arrow-trend-down",
												family: "sharp",
												style: "solid",
											})}
											className="text-danger"
										/>
									)}
									<span className={isPositiveChange ? "text-success" : "text-danger"}>
										{formatPercentage(changePercentage, true)}
									</span>
								</div>
								{changeTitle && (
									<span className="text-text-2 text-xs mt-0.5">{changeTitle}</span>
								)}
							</div>
						)}
					</div>

				{trendData && (
					<div className="mt-0 -mx-4">
						<SparkLine 
							data={trendData} 
							width={sparklineWidth}
							height={40}
							showAsArea={true}
							showForecast={showForecast}
						/>
					</div>
				)}
				</div>
			</div>
		</div>
	);	
};

